import React from 'react';
import { motion } from 'framer-motion';
import { FaLinkedin, FaUser } from 'react-icons/fa';
import BlurText from './ui/BlurText';

const StudentsSection = ({ students }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  // Remove duplicates - keep only unique students by studentid
  const uniqueStudents = students.reduce((acc, student) => {
    if (!acc.some(s => s.studentid === student.studentid)) {
      acc.push(student);
    }
    return acc;
  }, []);

  // Group students by batch
  const studentsByBatch = uniqueStudents.reduce((acc, student) => {
    const batch = student.batch || 'Core Team';
    if (!acc[batch]) acc[batch] = [];
    acc[batch].push(student);
    return acc;
  }, {});

  const batches = Object.keys(studentsByBatch).sort().reverse();

  return (
    <section id="team" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-16 md:mb-20"
        >
          <BlurText
            text="Meet The Team"
            className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 sm:mb-6 text-center tracking-tight text-white"
            childClassName="text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-400"
            delay={150}
            animateBy="words"
            direction="top"
          />
          <p className="text-slate-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-4">
            The brilliant minds and creative souls behind our innovative projects.
          </p>
        </motion.div>

        {batches.map((batch) => (
          <div key={batch} className="mb-20 last:mb-0">
            <div className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-10">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent"></div>
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-sm sm:text-base md:text-xl font-mono text-cyan-400 uppercase tracking-[0.1em] sm:tracking-[0.2em] px-3 sm:px-4 py-1 border border-cyan-500/20 rounded-full bg-cyan-500/5 backdrop-blur-sm whitespace-nowrap"
              >
                Batch {batch}
              </motion.h3>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent"></div>
            </div>

            <motion.div
              className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {studentsByBatch[batch].map((student) => (
                <StudentCard key={student.studentid} student={student} />
              ))}
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
};

const StudentCard = ({ student }) => {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 100 } }
      }}
      className="group relative"
    >
      <div className="absolute -inset-0.5 bg-gradient-to-b from-cyan-500/50 to-purple-500/50 rounded-2xl opacity-0 group-hover:opacity-100 transition duration-500 blur-sm pointer-events-none"></div>
      <div className="relative h-full bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-col items-center text-center transition-transform duration-300 group-hover:-translate-y-1">

        {/* Avatar */}
        <div className="relative w-24 h-24 mb-4">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full opacity-20 group-hover:opacity-40 transition-opacity blur-md"></div>
          <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-slate-700 group-hover:border-cyan-400/50 transition-colors">
            {student.studentimage ? (
              <img
                src={`/files/${student.studentimage}`}
                alt={student.studentname}
                className="w-full h-full object-cover transition-all duration-500"
              />
            ) : (
              <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                <FaUser className="text-3xl text-slate-600" />
              </div>
            )}
          </div>

          {/* Status Dot */}
          <div className="absolute bottom-1 right-1 w-4 h-4 bg-slate-900 rounded-full flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Info */}
        <h4 className="font-bold text-slate-200 mb-4 group-hover:text-cyan-400 transition-colors">
          {student.studentname}
        </h4>

        {/* Socials */}
        <div className="mt-auto pt-3 w-full border-t border-slate-800/50 flex justify-center">
          {student.linkedin ? (
            <a
              href={student.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-10 text-slate-400 hover:text-cyan-400 transition-colors p-2 hover:bg-cyan-500/10 rounded-full"
            >
              <FaLinkedin className="text-lg" />
            </a>
          ) : (
            <div className="h-8"></div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default StudentsSection;
